import { ActionTextAttachmentNode, Extension } from "@37signals/lexxy"
import { $getNodeByKey, $getSelection, $isNodeSelection, defineExtension } from "lexical"

class LinkedImageAttachmentNode extends ActionTextAttachmentNode {
  /**
   * Returns the Lexical node type identifier used for serialization and registration.
   * @returns {string} The unique node type for linked image attachments.
   */
  static getType() {
    return "linked_image_attachment"
  }

  /**
   * Creates a duplicate node instance with the same attributes and key.
    * Example: Lexical calls `clone()` when it needs a new editor-state snapshot
    * during an update and carries this node forward into the next immutable state.
   * @param {LinkedImageAttachmentNode} node - The node to clone.
   * @returns {LinkedImageAttachmentNode} A cloned linked image attachment node.
   */
  static clone(node) {
    return new LinkedImageAttachmentNode({ ...node }, node.__key)
  }

  /**
   * Rehydrates a linked image node from serialized editor-state JSON.
   *
   * In Lexical, JSON import/export is the persistence format for editor state
   * (for example, saving to a database or sending over the network), separate
   * from DOM import/export used for HTML conversion.
   * @param {Object} serializedNode - The serialized node payload.
   * @returns {LinkedImageAttachmentNode} A linked image attachment node instance.
   */
  static importJSON(serializedNode) {
    return new LinkedImageAttachmentNode({ ...serializedNode })
  }

  /**
   * Defines how Lexical should convert incoming HTML/DOM into editor nodes.
   *
   * "DOM import" in Lexical/Lexxy means parsing existing markup (for example,
   * pasted HTML or server-rendered content) and mapping matched elements to
   * Lexical node instances. This map registers conversions for image/video
   * attachments and this custom attachment tag, extracting attributes from the
   * element and from its parent anchor to hydrate `external` links.
   * @returns {Object} A Lexical DOM conversion map keyed by tag names.
   */
  static importDOM() {
    const { img, video } = ActionTextAttachmentNode.importDOM()

    return {
      img,
      video,
      [this.TAG_NAME]: () => ({
        conversion: (attachment) => {
          // const anchor = attachment.closest("a")

          return {
            node: new LinkedImageAttachmentNode({
              sgid: attachment.getAttribute("sgid"),
              src: attachment.getAttribute("url"),
              previewable: attachment.getAttribute("previewable"),
              altText: attachment.getAttribute("alt"),
              caption: attachment.getAttribute("caption"),
              contentType: attachment.getAttribute("content-type"),
              fileName: attachment.getAttribute("filename"),
              fileSize: attachment.getAttribute("filesize"),
              width: attachment.getAttribute("width"),
              height: attachment.getAttribute("height"),
              external: attachment.getAttribute("external")
            })
          }
        },
        priority: 1
      })
    }
  }

  /**
   * This adds the external url to the figure html in edit mode. This is not
   * necessary for things to work, but it's nice in development to be able to
   * see if external is being passed around correctly. The external value
   * that matters is stored in the JSON state of the editor.
   * @returns {HTMLElement} figure element with our external url
   */
  createDOM() {
    const fig = super.createDOM()
    fig.dataset['external'] = this.external

    return fig
  }

  /**
   * Initializes a linked image node and stores an optional external URL.
   * @param {Object} attributes - Attachment attributes for the base node.
   * @param {string} [attributes.external] - External URL to wrap the image with.
   * @param {string} [key] - Optional Lexical node key.
   * @returns {void}
   */
  constructor({ external, alignment, ...attributes }, key) {
    super({ ...attributes }, key)
    this.external = external || ""
    this.alignment = alignment || "left"
  }

  // createDOM() {
  //   return super.createDOM()
  // }

  /**
    * Converts this Lexical node into DOM elements for HTML output.
    *
    * "DOM export" in Lexical/Lexxy is the inverse of DOM import: it turns the
    * editor's internal node model into concrete HTML/DOM for rendering, copying,
    * or serialization.
    * @returns {{element: HTMLElement|null}} The exported DOM payload for this node.
   */
  exportDOM() {
    const exported = super.exportDOM()
    const attachment = exported.element

    if (!attachment) return exported

    // This is where the external url is applied to the HTML that is sent to the
    // server to be persisted.
    //
    if (this.external) {
      attachment.setAttribute("external", this.external)
    }

    return { element: attachment }
  }

  /**
    * Serializes this node into Lexical editor-state JSON.
    *
    * This JSON is used for durable storage of editor content and for restoring
    * state later via `importJSON`, including the custom `external` link field.
   * @returns {Object} Serialized node data for persistence.
   */
  exportJSON() {
    return {
      ...super.exportJSON(),
      external: this.external,
      alignment: this.alignment
    }
  }
}

export class LinkedImageExtension extends Extension {
  get enabled() {
    return this.editorElement.supportsRichText && this.editorElement.supportsAttachments
  }

  get allowedElements() {
    return [{ tag: LinkedImageAttachmentNode.TAG_NAME, attributes: ["external"] }]
  }

  get lexicalExtension() {
    return defineExtension({
      name: "mstone/image-link",
      nodes: [
        LinkedImageAttachmentNode,
        {
          replace: ActionTextAttachmentNode,
          with: (node) => {
            // Only use LinkedImageAttachmentNode for previewable images
            if (node.isPreviewableImage) {
              return new LinkedImageAttachmentNode({ ...node })
            }
            // Otherwise keep the original ActionTextAttachmentNode
            return node
          },
          withKlass: LinkedImageAttachmentNode
        }
      ]
    })
  }

  initializeToolbar(toolbar) {
    const imageButton = toolbar.querySelector("button[name=image]")
    if (!imageButton) return

    this.dropdown = this.#buildDropdown()
    imageButton.insertAdjacentElement("afterend", this.dropdown)

    this.trigger = this.dropdown.querySelector("[data-dropdown-trigger]")
    this.urlInput = this.dropdown.querySelector("input[type=url]")

    this.dropdown.querySelector("form").addEventListener("submit", this.#handleFormSubmit)
    this.dropdown.querySelector("[data-action=unlink]").addEventListener("click", this.#handleUnlink)
    this.dropdown.querySelector("[data-dropdown-panel]").addEventListener("keydown", this.#handleKeyDown)

    // Sync URL input when dropdown opens
    const panel = this.dropdown.querySelector("[data-dropdown-panel]")
    const observer = new MutationObserver(() => {
      if (!panel.hidden) this.#handleDropdownOpen()
    })
    observer.observe(panel, { attributes: true, attributeFilter: ["hidden"] })

    this.unregisterUpdateListener = this.editorElement.editor.registerUpdateListener(() => {
      this.#syncState()
    })

    const originalDispose = toolbar.dispose?.bind(toolbar)
    toolbar.dispose = () => {
      observer.disconnect()
      this.unregisterUpdateListener?.()
      this.unregisterUpdateListener = null
      this.dropdown = null
      this.trigger = null
      this.urlInput = null
      originalDispose?.()
    }

    this.#syncState()
  }

  #buildDropdown() {
    const dropdown = document.createElement("lexxy-toolbar-dropdown")
    dropdown.className = "lexxy-editor__toolbar-dropdown"
    dropdown.dataset["position"] = 2
    dropdown.innerHTML = `
      <button data-dropdown-trigger class="lexxy-editor__toolbar-button" type="button" name="image-link" title="Link selected image" aria-label="Link selected image" aria-haspopup="dialog" aria-expanded="false">
        <i class="fa_icon fa-solid fa-link-simple"></i>
      </button>
      <div data-dropdown-panel role="dialog" aria-label="Link image" hidden>
        <form method="dialog">
          <input type="url" placeholder="Enter a URL…" class="input">
          <div class="lexxy-editor__toolbar-dropdown-actions">
            <button type="submit" class="lexxy-editor__toolbar-button" value="link" tabindex="-1">Link</button>
            <button type="button" class="lexxy-editor__toolbar-button" value="unlink" tabindex="-1" data-action="unlink">Unlink</button>
          </div>
        </form>
      </div>
    `
    return dropdown
  }

  #handleDropdownOpen = () => {
    if (!this.selectedNodeKey) {
      this.dropdown.close()
      return
    }

    let currentUrl = ""
    this.editorElement.editor.getEditorState().read(() => {
      const node = $getNodeByKey(this.selectedNodeKey)
      currentUrl = node?.external || ""
    })

    this.urlInput.value = currentUrl
    requestAnimationFrame(() => this.urlInput?.focus())
  }

  #handleFormSubmit = (event) => {
    event.preventDefault()
    const url = this.urlInput.value.trim()

    this.editorElement.editor.update(() => {
      const node = $getNodeByKey(this.selectedNodeKey)
      if (node instanceof LinkedImageAttachmentNode) node.getWritable().external = url
    })

    this.dropdown.close()
  }

  #handleUnlink = () => {
    this.editorElement.editor.update(() => {
      const node = $getNodeByKey(this.selectedNodeKey)
      if (node instanceof LinkedImageAttachmentNode) node.getWritable().external = ""
    })

    this.dropdown.close()
  }

  #handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation()
      this.dropdown.close()
    }
  }

  // Handles the state of the toolbar button
  #syncState() {
    if (!this.trigger) return
    if (this.dropdown?.querySelector("[data-dropdown-panel]")?.hidden === false) return  // freeze state while dropdown is open

    let selectedNodeKey = null
    let isLinked = false

    this.editorElement.editor.getEditorState().read(() => {
      const node = this.#selectedImageAttachmentNode()
      if (node) {
        selectedNodeKey = node.getKey()
        isLinked = Boolean(node.external)
      }
    })

    this.selectedNodeKey = selectedNodeKey
    this.trigger.setAttribute("aria-disabled", `${!selectedNodeKey}`)
    this.trigger.setAttribute("aria-pressed", `${isLinked}`)
  }

  // Check if the selected node is an image attachment
  #selectedImageAttachmentNode() {
    const selection = $getSelection()
    if (!$isNodeSelection(selection)) return null

    return selection.getNodes().find((node) => {
      return node instanceof ActionTextAttachmentNode && node.isPreviewableImage
    }) || null
  }
}
